import { createFileRoute, useNavigate, Link, redirect } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { registerService } from '@/features/auth/services/register.service';
import { platformSettingsService } from '@/features/branding/services/platform-settings.service';
import { useAuthStore } from '@/store/auth.store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const registerSchema = z
  .object({
    companyName: z.string().min(2, 'Nome da empresa deve ter no mínimo 2 caracteres'),
    adminEmail: z.string().email('Email inválido'),
    adminPassword: z
      .string()
      .min(8, 'Senha deve ter no mínimo 8 caracteres')
      .regex(/[a-z]/, 'Senha deve conter pelo menos uma letra minúscula')
      .regex(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
      .regex(/\d/, 'Senha deve conter pelo menos um número')
      .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, 'Senha deve conter pelo menos um caractere especial'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.adminPassword === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export const Route = createFileRoute('/register')({
  beforeLoad: async () => {
    const token = localStorage.getItem('token');
    if (token) throw redirect({ to: '/' });

    try {
      const settings = await platformSettingsService.getPublic();
      if (settings.public_signup_enabled === false) {
        throw redirect({ to: '/login' });
      }
    } catch (err: any) {
      // Re-lançar redirects do TanStack Router
      if (err?.isRedirect) throw err;
      // Se a API falhar, deixar passar (o backend ainda vai bloquear no submit)
    }
  },
  component: RegisterPage,
});

function RegisterPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [slugPreview, setSlugPreview] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const companyName = watch('companyName', '');

  useEffect(() => {
    setSlugPreview(generateSlug(companyName));
  }, [companyName]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      queryClient.clear();
      localStorage.removeItem('token');
      localStorage.removeItem('auth-storage');
      localStorage.removeItem('user');

      const response = await registerService.register({
        companyName: data.companyName,
        adminEmail: data.adminEmail,
        adminPassword: data.adminPassword,
      });

      setAuth(response.user, response.accessToken);
      toast.success(`Empresa "${response.tenant.name}" criada com sucesso!`);
      navigate({ to: '/my-dashboard' });
    } catch (error: any) {
      if (error?.response?.status === 429) {
        toast.error('Muitas tentativas de cadastro. Tente novamente mais tarde.', { duration: 8000 });
        return;
      }
      const message =
        error?.response?.data?.message || 'Erro ao criar conta. Tente novamente.';
      toast.error(Array.isArray(message) ? message[0] : message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-page-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center">Criar conta</CardTitle>
          <CardDescription className="text-center">
            Crie sua empresa e comece a usar o sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Nome da empresa</Label>
              <Input
                id="companyName"
                type="text"
                placeholder="Empresa XYZ Ltda"
                {...register('companyName')}
              />
              {errors.companyName && (
                <p className="text-sm text-destructive">{errors.companyName.message}</p>
              )}
              {slugPreview && (
                <p className="text-xs text-muted-foreground font-mono">
                  Identificador: <span className="text-foreground">{slugPreview}</span>
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminEmail">Email do administrador</Label>
              <Input
                id="adminEmail"
                type="email"
                placeholder="admin@empresa.com"
                {...register('adminEmail')}
              />
              {errors.adminEmail && (
                <p className="text-sm text-destructive">{errors.adminEmail.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="adminPassword">Senha</Label>
              <div className="relative">
                <Input
                  id="adminPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  {...register('adminPassword')}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.adminPassword && (
                <p className="text-sm text-destructive">{errors.adminPassword.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar senha</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="••••••••"
                  {...register('confirmPassword')}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  {showConfirm ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Criando conta...' : 'Criar conta'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Já tem conta?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Entrar
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
